import { BaseService } from './base'
import { APIPath } from 'utility/constant'

const rotationRoot = `${APIPath.server}User/Rotation`

/** Normalize schedule id from create/get API responses (number, string, or object). */
export function extractRotationScheduleId(response) {
    if (response == null) return null
    if (typeof response === 'number' && !Number.isNaN(response)) return response
    if (typeof response === 'string' && response.trim() !== '') {
        const parsed = parseInt(response, 10)
        return Number.isNaN(parsed) ? null : parsed
    }
    if (typeof response === 'object') {
        const id =
            response.RotationScheduleId ??
            response.Id ??
            response.id ??
            response.ScheduleId
        if (id != null && id !== '') {
            const parsed = typeof id === 'number' ? id : parseInt(id, 10)
            return Number.isNaN(parsed) ? null : parsed
        }
    }
    return null
}

/** API TemplateId is a string; multiple ids are semicolon-separated. */
export function toRotationTemplateIdString(ids) {
    if (ids == null) return null
    const list = Array.isArray(ids) ? ids : [ids]
    const filtered = list.filter(id => id != null && id !== '')
    return filtered.length ? filtered.join(';') : null
}

export function parseRotationTemplateIds(templateId) {
    if (templateId == null || templateId === '') return []
    if (Array.isArray(templateId)) {
        return templateId.filter(id => id != null && id !== '')
    }
    return String(templateId)
        .split(';')
        .map(id => id.trim())
        .filter(Boolean)
        .map(id => {
            const parsed = parseInt(id, 10)
            return Number.isNaN(parsed) ? id : parsed
        })
}

class RotationServiceClass {
    getSchedules(userId) {
        // Add cache-buster query param so GET is not served from cache
        const cacheBuster = Date.now()
        return BaseService.get(
            `${rotationRoot}/${userId}/Schedules?ts=${cacheBuster}`,
        )
    }

    getScheduleById(userId, scheduleId) {
        return BaseService.get(
            `${rotationRoot}/${userId}/Schedules/${scheduleId}`,
        )
    }

    createSchedule(userId, data) {
        return BaseService.post(`${rotationRoot}/${userId}/Schedules`, data)
    }

    updateSchedule(userId, scheduleId, data) {
        return BaseService.put(
            `${rotationRoot}/${userId}/Schedules/${scheduleId}`,
            data,
        )
    }

    deleteSchedule(userId, scheduleId) {
        return BaseService.remove(
            `${rotationRoot}/${userId}/Schedules/${scheduleId}`,
        )
    }

    getDetails(userId, scheduleId) {
        return BaseService.get(
            `${rotationRoot}/${userId}/Schedules/${scheduleId}/Details`,
        )
    }

    getDetailById(userId, detailId) {
        return BaseService.get(
            `${rotationRoot}/${userId}/Details/${detailId}`,
        )
    }

    createDetail(userId, data) {
        return BaseService.post(`${rotationRoot}/${userId}/Details`, data)
    }

    updateDetail(userId, detailId, data) {
        return BaseService.put(
            `${rotationRoot}/${userId}/Details/${detailId}`,
            data,
        )
    }

    deleteDetail(userId, detailId) {
        return BaseService.remove(
            `${rotationRoot}/${userId}/Details/${detailId}`,
        )
    }

    getRotationViewer(userId, startDate, endDate) {
        return BaseService.get(
            `${rotationRoot}/${userId}/Viewer?startDate=${startDate}&endDate=${endDate}`,
        )
    }

    getEmployees(userId) {
        return BaseService.get(`${rotationRoot}/${userId}/Employees`)
    }

    getShifts(userId) {
        return BaseService.get(`${rotationRoot}/${userId}/Shifts`)
    }

    getWorkPlaces(userId) {
        return BaseService.get(`${rotationRoot}/${userId}/WorkPlaces`)
    }
}

const RotationService = new RotationServiceClass()

export async function resolveRotationScheduleIdAfterCreate(
    userId,
    createdResponse,
    scheduleTitle,
) {
    const directId = extractRotationScheduleId(createdResponse)
    if (directId) return directId

    const scheduleList = (await RotationService.getSchedules(userId)) || []

    const byTitle = scheduleList.find(
        schedule =>
            (schedule?.Title || '').trim() === (scheduleTitle || '').trim(),
    )
    const titleMatchId = extractRotationScheduleId(byTitle)
    if (titleMatchId) return titleMatchId

    const ids = scheduleList
        .map(schedule => extractRotationScheduleId(schedule))
        .filter(id => id != null)
    return ids.length ? Math.max(...ids) : null
}

Object.freeze(RotationService)
export { RotationService }
